import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import { userService, type User, type UserQueryParams, type CreateUserRequest, type UpdateUserRequest } from '@/services/admin/user.service';
import type { PagedResult } from '@/services/base/base.service';
import { toast } from 'sonner';

/**
 * Query keys for user-related queries
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: UserQueryParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  roles: (id: string) => [...userKeys.detail(id), 'roles'] as const,
  activity: (id: string) => [...userKeys.detail(id), 'activity'] as const,
};

/**
 * Hook to fetch paginated list of users
 */
export function useUsers(
  params: UserQueryParams = {}
): UseQueryResult<PagedResult<User>, Error> {
  return useQuery({
    queryKey: userKeys.list(params),
    queryFn: () => userService.getUsers(params),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch single user by ID
 */
export function useUser(
  id: string,
  options?: { enabled?: boolean }
): UseQueryResult<User, Error> {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => userService.getUser(id),
    enabled: options?.enabled !== false && !!id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to fetch user roles
 */
export function useUserRoles(
  id: string,
  options?: { enabled?: boolean }
): UseQueryResult<string[], Error> {
  return useQuery({
    queryKey: userKeys.roles(id),
    queryFn: () => userService.getUserRoles(id),
    enabled: options?.enabled !== false && !!id,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to create a new user
 */
export function useCreateUser(): UseMutationResult<User, Error, CreateUserRequest> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateUserRequest) => userService.createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create user: ${error.message}`);
    },
  });
}

/**
 * Hook to update an existing user
 */
export function useUpdateUser(): UseMutationResult<User, Error, { id: string; data: UpdateUserRequest }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
      userService.updateUser(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update user: ${error.message}`);
    },
  });
}

/**
 * Hook to delete a user
 */
export function useDeleteUser(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete user: ${error.message}`);
    },
  });
}

/**
 * Hook to update user roles
 */
export function useUpdateUserRoles(): UseMutationResult<void, Error, { id: string; roleNames: string[] }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roleNames }: { id: string; roleNames: string[] }) =>
      userService.updateUserRoles(id, roleNames),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.roles(id) });
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      toast.success('User roles updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update user roles: ${error.message}`);
    },
  });
}

/**
 * Hook to lock a user account
 */
export function useLockUser(): UseMutationResult<User, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.lockUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User account locked successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to lock user: ${error.message}`);
    },
  });
}

/**
 * Hook to unlock a user account
 */
export function useUnlockUser(): UseMutationResult<User, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.unlockUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User account unlocked successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to unlock user: ${error.message}`);
    },
  });
}

/**
 * Hook to activate a user account
 */
export function useActivateUser(): UseMutationResult<User, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.activateUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User account activated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to activate user: ${error.message}`);
    },
  });
}

/**
 * Hook to deactivate a user account
 */
export function useDeactivateUser(): UseMutationResult<User, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userService.deactivateUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      toast.success('User account deactivated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to deactivate user: ${error.message}`);
    },
  });
}

/**
 * Hook to set user password (admin only)
 */
export function useSetUserPassword(): UseMutationResult<void, Error, { id: string; password: string }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      userService.setUserPassword(id, password),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: userKeys.detail(id) });
      toast.success('Password reset successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to reset password: ${error.message}`);
    },
  });
}
